import React from 'react';
import DataEntryTabs from '../DataEntryTabs';
import { TabProps } from './types';
import { getPriorityClasses, getPriorityIndicator, countSection2LMFields, countSection2ManualFields } from './helpers';

const Tab1SwingCharacteristics: React.FC<TabProps> = ({
  formData,
  updateNestedField,
  updateFormData,
}) => {
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">Section 2: Swing Characteristics</h3>

      <DataEntryTabs
        sectionName="Swing Characteristics"
        lmFieldCount={countSection2LMFields(formData)}
        manualFieldCount={countSection2ManualFields(formData)}
        lmTabContent={
          <div className="space-y-6">
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
              <p className="text-sm text-blue-700">
                💡 <strong>Launch Monitor Data</strong> - Enter precise measurements from TrackMan, GCQuad, or similar devices for the most accurate recommendations.
              </p>
            </div>

            {/* Driver LM Data */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">🏌️ Driver Swing Metrics</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    Swing Speed (mph){getPriorityIndicator('driver_swing_data.swing_speed_driver')}
                  </label>
                  <input
                    type="number"
                    min="50"
                    max="150"
                    value={formData.driver_swing_data?.swing_speed_driver || ''}
                    onChange={(e) => updateNestedField('driver_swing_data', {
                      swing_speed_driver: e.target.value ? parseFloat(e.target.value) : undefined
                    })}
                    className={getPriorityClasses('driver_swing_data.swing_speed_driver', !formData.driver_swing_data?.swing_speed_driver)}
                  />
                </div>
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    Attack Angle (degrees){getPriorityIndicator('driver_swing_data.attack_angle_driver')}
                  </label>
                  <input
                    type="number"
                    min="-10"
                    max="10"
                    step="0.1"
                    value={formData.driver_swing_data?.attack_angle_driver || ''}
                    onChange={(e) => updateNestedField('driver_swing_data', {
                      attack_angle_driver: e.target.value ? parseFloat(e.target.value) : undefined
                    })}
                    className={getPriorityClasses('driver_swing_data.attack_angle_driver', !formData.driver_swing_data?.attack_angle_driver)}
                  />
                  <p className="text-xs text-gray-500 mt-1">💡 Negative = hitting down, Positive = hitting up</p>
                </div>
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    Club Path (degrees)
                  </label>
                  <input
                    type="number"
                    min="-15"
                    max="15"
                    step="0.1"
                    value={formData.driver_swing_data?.club_path_driver || ''}
                    onChange={(e) => updateNestedField('driver_swing_data', {
                      club_path_driver: e.target.value ? parseFloat(e.target.value) : undefined
                    })}
                    className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                  />
                  <p className="text-xs text-gray-500 mt-1">💡 Positive = out-to-in, Negative = in-to-out</p>
                </div>
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    Face Angle (degrees)
                  </label>
                  <input
                    type="number"
                    min="-15"
                    max="15"
                    step="0.1"
                    value={formData.driver_swing_data?.face_angle_driver || ''}
                    onChange={(e) => updateNestedField('driver_swing_data', {
                      face_angle_driver: e.target.value ? parseFloat(e.target.value) : undefined
                    })}
                    className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                  />
                  <p className="text-xs text-gray-500 mt-1">💡 Club face angle at impact</p>
                </div>
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    Dynamic Loft (degrees)
                  </label>
                  <input
                    type="number"
                    min="8"
                    max="20"
                    step="0.1"
                    value={formData.driver_swing_data?.dynamic_loft_driver || ''}
                    onChange={(e) => updateNestedField('driver_swing_data', {
                      dynamic_loft_driver: e.target.value ? parseFloat(e.target.value) : undefined
                    })}
                    className="mt-1 block w-full text-base px-4 py-3 md:py-2 min-h-[44px] rounded-md shadow-sm border-gray-300 focus:border-green-ink focus:ring-green-ink"
                  />
                  <p className="text-xs text-gray-500 mt-1">💡 Actual loft delivered at impact</p>
                </div>
              </div>
            </div>

            {/* 7-Iron LM Data */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">🏌️ 7-Iron Swing Metrics</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    Swing Speed (mph){getPriorityIndicator('iron_swing_data.swing_speed_7iron')}
                  </label>
                  <input
                    type="number"
                    min="40"
                    max="120"
                    value={formData.iron_swing_data?.swing_speed_7iron || ''}
                    onChange={(e) => updateNestedField('iron_swing_data', {
                      swing_speed_7iron: e.target.value ? parseFloat(e.target.value) : undefined
                    })}
                    className={getPriorityClasses('iron_swing_data.swing_speed_7iron', !formData.iron_swing_data?.swing_speed_7iron)}
                  />
                </div>
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    Attack Angle (degrees){getPriorityIndicator('iron_swing_data.attack_angle_irons')}
                  </label>
                  <input
                    type="number"
                    min="-10"
                    max="5"
                    step="0.1"
                    value={formData.iron_swing_data?.attack_angle_irons || ''}
                    onChange={(e) => updateNestedField('iron_swing_data', {
                      attack_angle_irons: e.target.value ? parseFloat(e.target.value) : undefined
                    })}
                    className={getPriorityClasses('iron_swing_data.attack_angle_irons', !formData.iron_swing_data?.attack_angle_irons)}
                  />
                  <p className="text-xs text-gray-500 mt-1">💡 Irons typically -3° to -5° (descending blow)</p>
                </div>
              </div>
            </div>
          </div>
        }
        manualTabContent={
          <div className="space-y-6">
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <p className="text-sm text-yellow-700">
                ✏️ <strong>Manual Entry</strong> - Estimate these values if you don't have launch monitor data.
              </p>
            </div>

            {/* Driver Manual Data */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">Driver Swing Data</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    Driver Swing Speed (mph){getPriorityIndicator('driver_swing_data.swing_speed_driver')}
                  </label>
                  <input
                    type="number"
                    min="50"
                    max="150"
                    value={formData.driver_swing_data?.swing_speed_driver || ''}
                    onChange={(e) => updateNestedField('driver_swing_data', {
                      swing_speed_driver: e.target.value ? parseFloat(e.target.value) : undefined
                    })}
                    className={getPriorityClasses('driver_swing_data.swing_speed_driver', !formData.driver_swing_data?.swing_speed_driver)}
                  />
                </div>
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    Driver Attack Angle (degrees){getPriorityIndicator('driver_swing_data.attack_angle_driver')}
                  </label>
                  <input
                    type="number"
                    min="-10"
                    max="10"
                    step="0.1"
                    value={formData.driver_swing_data?.attack_angle_driver || ''}
                    onChange={(e) => updateNestedField('driver_swing_data', {
                      attack_angle_driver: e.target.value ? parseFloat(e.target.value) : undefined
                    })}
                    className={getPriorityClasses('driver_swing_data.attack_angle_driver', !formData.driver_swing_data?.attack_angle_driver)}
                  />
                </div>
              </div>
            </div>

            {/* Iron Manual Data */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">Iron Swing Data</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    7-Iron Swing Speed (mph){getPriorityIndicator('iron_swing_data.swing_speed_7iron')}
                  </label>
                  <input
                    type="number"
                    min="40"
                    max="120"
                    value={formData.iron_swing_data?.swing_speed_7iron || ''}
                    onChange={(e) => updateNestedField('iron_swing_data', {
                      swing_speed_7iron: e.target.value ? parseFloat(e.target.value) : undefined
                    })}
                    className={getPriorityClasses('iron_swing_data.swing_speed_7iron', !formData.iron_swing_data?.swing_speed_7iron)}
                  />
                </div>
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    7-Iron Attack Angle (degrees){getPriorityIndicator('iron_swing_data.attack_angle_irons')}
                  </label>
                  <input
                    type="number"
                    min="-10"
                    max="5"
                    step="0.1"
                    value={formData.iron_swing_data?.attack_angle_irons || ''}
                    onChange={(e) => updateNestedField('iron_swing_data', {
                      attack_angle_irons: e.target.value ? parseFloat(e.target.value) : undefined
                    })}
                    className={getPriorityClasses('iron_swing_data.attack_angle_irons', !formData.iron_swing_data?.attack_angle_irons)}
                  />
                </div>
              </div>
            </div>
          </div>
        }
      />

      {/* Swing Tempo — self-reported, applies regardless of LM/manual entry */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-1">Swing Tempo</h4>
        <p className="text-xs text-gray-500 mb-3">How would you describe your transition from backswing to downswing?</p>
        <div className="flex gap-3">
          {(['smooth', 'moderate', 'aggressive'] as const).map((tempo) => {
            const labels: Record<string, string> = {
              smooth: 'Smooth',
              moderate: 'Moderate',
              aggressive: 'Aggressive',
            };
            const descs: Record<string, string> = {
              smooth: 'Unhurried, wide arc',
              moderate: 'Balanced transition',
              aggressive: 'Quick, forceful load',
            };
            // No pre-selected default — an unmade choice must look unmade, or nobody makes it.
            const selected = formData.swing_tempo === tempo;
            return (
              <button
                key={tempo}
                type="button"
                onClick={() => updateFormData({ swing_tempo: tempo })}
                className={`flex-1 py-2 px-3 rounded-md border text-sm font-medium transition-colors ${
                  selected
                    ? 'bg-green-ink border-green-ink text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-green-ink'
                }`}
              >
                <div>{labels[tempo]}</div>
                <div className={`text-xs mt-0.5 ${selected ? 'text-green-ink' : 'text-gray-400'}`}>{descs[tempo]}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* F8/B9: transition_timing — checked first by driver_shaft_responsiveness() (more precise
          vocabulary than Swing Tempo above), swing_tempo above is only the fallback. Was collected
          on 1.4% of profiles because no form field ever asked for it. */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-1">Transition Timing</h4>
        <p className="text-xs text-gray-500 mb-3">
          More precise than Swing Tempo above — how the club loads right as you start down. If you're not sure, leave this blank and Swing Tempo will be used instead.
        </p>
        <div className="flex gap-3">
          {(['smooth', 'moderate', 'quick', 'aggressive'] as const).map((timing) => {
            const labels: Record<string, string> = {
              smooth: 'Smooth',
              moderate: 'Moderate',
              quick: 'Quick',
              aggressive: 'Aggressive',
            };
            const selected = formData.transition_timing === timing;
            return (
              <button
                key={timing}
                type="button"
                onClick={() => updateFormData({ transition_timing: selected ? undefined : timing })}
                className={`flex-1 py-2 px-3 rounded-md border text-sm font-medium transition-colors ${
                  selected
                    ? 'bg-green-ink border-green-ink text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-green-ink'
                }`}
              >
                {labels[timing]}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Tab1SwingCharacteristics;
